import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { SalonProvider, useSalon } from './contexts/SalonContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import Login from './pages/Login';
import Register from './pages/Register';
import MainLayout from './components/Layout/MainLayout';
import { useAuth } from './contexts/AuthContext';
import Dashboard from './components/Dashboard/Dashboard';
import UserOnboardingDashboard from './pages/UserOnboardingDashboard';
import BookingList from './components/Booking/BookingList';
import Employees from './components/Employee/Employees';
import UserProfile from './components/User/UserProfile';
import OnlineBooking from './pages/OnlineBooking';
import BookingSettings from './components/Booking/BookingSettings';
import BusinessSettings from './pages/BusinessSettings';
import CustomerList from './pages/CustomerList';
import CustomerProfile from './pages/CustomerProfile';
import CheckinPage from './pages/CheckinPage';
import ReviewList from './pages/ReviewList';
import AdminRequests from './pages/AdminRequests';
import NotificationPage from './pages/NotificationPage';
import Earnings from './pages/Earnings';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';

// Component bảo vệ route
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const { t } = useLanguage();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        background: '#f8f9fa',
        color: '#475569',
        fontSize: '1.2rem',
        gap: '12px',
        fontWeight: 'bold'
      }}>
        <div className="loading-spinner" style={{ width: '44px', height: '44px', minWidth: '44px', minHeight: '44px', margin: '0 auto' }}></div>
        <p>{t('schedule.loading') || 'Đang tải dữ liệu gốc...'}</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return children;
};

// Component Wrapper để xử lý layout
const AppLayout = ({ children }: { children: React.ReactNode }) => {
  return (
    <ProtectedRoute>
      <MainLayout>
        {children}
      </MainLayout>
    </ProtectedRoute>
  );
};

// Smart Dashboard - điều hướng dựa trên việc user có business hay không
const SmartDashboard = () => {
  const { businesses, loading } = useSalon();
  const { t } = useLanguage();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        background: '#f8f9fa',
        color: '#475569',
        fontSize: '1.2rem',
        gap: '12px',
        fontWeight: 'bold'
      }}>
        <div className="loading-spinner" style={{ width: '44px', height: '44px', minWidth: '44px', minHeight: '44px', margin: '0 auto' }}></div>
        <p>{t('schedule.loading') || 'Kiểm tra cửa hàng...'}</p>
      </div>
    );
  }

  // Nếu user chưa có business → hiển thị Onboarding Dashboard
  if (!businesses || businesses.length === 0) {
    return <UserOnboardingDashboard />;
  }

  // Nếu có business → hiển thị Dashboard bình thường với layout
  return (
    <MainLayout>
      <Dashboard />
    </MainLayout>
  );
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <WebSocketProvider>
          <NotificationProvider>
            <SalonProvider>
              <Router>
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />

                  {/* Public Online Booking - No login required, uses unique business code */}
                  <Route path="/book/:code" element={<OnlineBooking />} />

                  {/* Redirect root to dashboard */}
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />

                  <Route path="/dashboard" element={
                    <ProtectedRoute>
                      <SmartDashboard />
                    </ProtectedRoute>
                  } />

                  {/* Thêm route Profile */}
                  <Route path="/profile" element={
                    <AppLayout>
                      <UserProfile />
                    </AppLayout>
                  } />

                  <Route path="/bookings" element={
                    <AppLayout>
                      <BookingList />
                    </AppLayout>
                  } />

                  <Route path="/employees" element={
                    <AppLayout>
                      <Employees />
                    </AppLayout>
                  } />

                  <Route path="/earnings" element={
                    <AppLayout>
                      <Earnings />
                    </AppLayout>
                  } />

                  <Route path="/notifications" element={
                    <AppLayout>
                      <NotificationPage />
                    </AppLayout>
                  } />

                  <Route path="/settings/booking" element={
                    <ProtectedRoute>
                      <AppLayout>
                        <BookingSettings />
                      </AppLayout>
                    </ProtectedRoute>
                  } />

                  <Route path="/settings/business" element={
                    <ProtectedRoute>
                      <AppLayout>
                        <BusinessSettings />
                      </AppLayout>
                    </ProtectedRoute>
                  } />

                  <Route path="/customers" element={
                    <ProtectedRoute>
                      <AppLayout>
                        <CustomerList />
                      </AppLayout>
                    </ProtectedRoute>
                  } />

                  <Route path="/customers/:id" element={
                    <ProtectedRoute>
                      <AppLayout>
                        <CustomerProfile />
                      </AppLayout>
                    </ProtectedRoute>
                  } />

                  {/* Public QR Check-in page — no login required */}
                  <Route path="/checkin/:code" element={<CheckinPage />} />

                  {/* Reviews — Owner/Manager only */}
                  <Route path="/reviews" element={
                    <AppLayout>
                      <ReviewList />
                    </AppLayout>
                  } />


                  {/* Admin — Business Requests */}
                  <Route path="/admin/requests" element={
                    <AppLayout>
                      <AdminRequests />
                    </AppLayout>
                  } />

                </Routes>
              </Router>
            </SalonProvider>
          </NotificationProvider>
        </WebSocketProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;